import React, { useState } from 'react';
import { TrendingDown, Fuel, ChevronDown, ChevronUp } from 'lucide-react';
import './BestPricesPanel.css';

const getInitialExpanded = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth <= 768;
};

const normalizeBrand = (brand = '') => {
  return brand
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
};

const LOGO_BASE_PATH = `${process.env.PUBLIC_URL || ''}/logos`;

const getBrandLogo = (brand) => {
  const logoFiles = {
    circlek: 'circlek.png',
    gmcirclek: 'circlek.png',
    plungeslagunacirclek: 'circlek.png',
    vvarffcirclek: 'circlek.png',
    viada: 'viada.png',
    orlen: 'orlen.png',
    neste: 'neste.webp',
    nestelietuva: 'neste.webp',
    alausa: 'alausa.jpg',
    emsi: 'emsi.png',
    stateta: 'stateta.png',
    naftrus: 'naftrus.jpg',
    apsaga: 'apsaga.png',
    lukoil: 'lukoil.png',
    gotika: 'gotika.png',
    expres: 'expres.png',
    statoil: 'statoil.png',
    gulf: 'gulf.png',
    tauras: 'tauras.png',
    deglita: 'deglita.png',
    virsi: 'virsi.png',
    saurida: 'saurida.png',
    trevena: 'trevena.png',
    as24lietuva: 'as24.png',
    as24: 'as24.png',
    bpetroleum: 'bpetroleum.png',
    balticpetroleum: 'bpetroleum.png',
    jozita: 'jozita.jfif',
  };

  const normalized = normalizeBrand(brand);

  if (!normalized) return null;

  const directMatch = logoFiles[normalized];
  if (directMatch) {
    return `${LOGO_BASE_PATH}/${directMatch}`;
  }

  if (normalized.includes('circlek')) return `${LOGO_BASE_PATH}/circlek.png`;
  if (normalized.includes('orlen')) return `${LOGO_BASE_PATH}/orlen.png`;
  if (normalized.includes('viada')) return `${LOGO_BASE_PATH}/viada.png`;
  if (normalized.includes('neste')) return `${LOGO_BASE_PATH}/neste.webp`;
  if (normalized.includes('emsi')) return `${LOGO_BASE_PATH}/emsi.png`;
  if (normalized.includes('stateta')) return `${LOGO_BASE_PATH}/stateta.png`;
  if (normalized.includes('naftrus')) return `${LOGO_BASE_PATH}/naftrus.jpg`;
  if (normalized.includes('saurida')) return `${LOGO_BASE_PATH}/saurida.png`;
  if (normalized.includes('trevena')) return `${LOGO_BASE_PATH}/trevena.png`;
  if (normalized.includes('as24')) return `${LOGO_BASE_PATH}/as24.png`;
  if (normalized.includes('petroleum')) return `${LOGO_BASE_PATH}/bpetroleum.png`;
  if (normalized.includes('jozita')) return `${LOGO_BASE_PATH}/jozita.jfif`;

  return null;
};

function BestPricesPanel({ visibleStations, isExpanded = getInitialExpanded(), onExpandedChange, onStationLinkClick }) {
  const [internalExpanded, setInternalExpanded] = useState(getInitialExpanded);
  const expanded = typeof onExpandedChange === 'function' ? isExpanded : internalExpanded;
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientY);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientY);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isSwipeUp = distance > minSwipeDistance;
    const isSwipeDown = distance < -minSwipeDistance;
    
    if (isSwipeUp && !expanded) {
      if (typeof onExpandedChange === 'function') {
        onExpandedChange(true, 'best_prices_swipe');
      } else {
        setInternalExpanded(true);
      }
    } else if (isSwipeDown && expanded) {
      if (typeof onExpandedChange === 'function') {
        onExpandedChange(false, 'best_prices_swipe');
      } else {
        setInternalExpanded(false);
      }
    }
  };

  const handleToggleExpanded = () => {
    const next = !expanded;
    if (typeof onExpandedChange === 'function') {
      onExpandedChange(next, 'best_prices_header');
    } else {
      setInternalExpanded(next);
    }
  };

  const getBestPrices = () => {
    if (!visibleStations || visibleStations.length === 0) {
      return { gas: [], diesel: [] };
    }

    // Get top 5 cheapest gas prices
    const gasStations = visibleStations
      .filter(s => s.gasPrice > 0)
      .sort((a, b) => a.gasPrice - b.gasPrice)
      .slice(0, 5);

    // Get top 5 cheapest diesel prices
    const dieselStations = visibleStations
      .filter(s => s.dieselPrice > 0)
      .sort((a, b) => a.dieselPrice - b.dieselPrice)
      .slice(0, 5);

    return { gas: gasStations, diesel: dieselStations };
  };

  const bestPrices = getBestPrices();

  if (visibleStations.length === 0) {
    return (
      <div className={`best-prices-panel popup-panel ${expanded ? 'expanded' : 'collapsed'}`}>
        <div className="panel-header">
          <TrendingDown size={20} />
          <h3>Geriausios kainos</h3>
        </div>
        <div className="panel-empty">
          <p>Priartinkite žemėlapį, kad pamatytumėte geriausias kainas šiame regione</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`best-prices-panel popup-panel ${expanded ? 'expanded' : 'collapsed'}`}>
      <div 
        className="panel-header" 
        onClick={handleToggleExpanded}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <TrendingDown size={20} />
        <h3>Geriausios kainos</h3>
        <span className="station-count">{visibleStations.length} degalinės</span>
        {expanded ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
      </div>

      {expanded && <div className="panel-content">
        <div className="price-section">
          <h4><Fuel size={16} /> Benzinas 95</h4>
          {bestPrices.gas.length > 0 ? (
            <div className="price-list">
              {bestPrices.gas.map((station, index) => (
                <div key={station.id} className="price-item">
                  <div className="rank">{index + 1}</div>
                  <div className="brand-logo-large">
                    {getBrandLogo(station.brand) ? (
                      <img src={getBrandLogo(station.brand)} alt={station.brand} onError={(e) => e.target.style.display = 'none'} />
                    ) : '⛽'}
                  </div>
                  <div className="station-info">
                    <div className="station-name">{station.brand}</div>
                    <div className="station-address">{station.address}</div>
                    <a
                      className="station-detail-link"
                      href={`/degaline/${station.id}`}
                      onClick={() => {
                        if (typeof onStationLinkClick === 'function') {
                          onStationLinkClick(station, 'gas', index + 1);
                        }
                      }}
                    >Peržiūrėti degalinę</a>
                  </div>
                  <div className="price-badge">{station.gasPrice.toFixed(3)} €</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-data">Nėra duomenų</p>
          )}
        </div>

        <div className="price-section">
          <h4><Fuel size={16} /> Dyzelinas</h4>
          {bestPrices.diesel.length > 0 ? (
            <div className="price-list">
              {bestPrices.diesel.map((station, index) => (
                <div key={station.id} className="price-item">
                  <div className="rank">{index + 1}</div>
                  <div className="brand-logo-large">
                    {getBrandLogo(station.brand) ? (
                      <img src={getBrandLogo(station.brand)} alt={station.brand} onError={(e) => e.target.style.display = 'none'} />
                    ) : '⛽'}
                  </div>
                  <div className="station-info">
                    <div className="station-name">{station.brand}</div>
                    <div className="station-address">{station.address}</div>
                    <a
                      className="station-detail-link"
                      href={`/degaline/${station.id}`}
                      onClick={() => {
                        if (typeof onStationLinkClick === 'function') {
                          onStationLinkClick(station, 'diesel', index + 1);
                        }
                      }}
                    >Peržiūrėti degalinę</a>
                  </div>
                  <div className="price-badge">{station.dieselPrice.toFixed(3)} €</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-data">Nėra duomenų</p>
          )}
        </div>
      </div>}
    </div>
  );
}

export default BestPricesPanel;
